import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import moment from "moment";
import Swal from "sweetalert2";
import ReactPaginate from "react-paginate";
import CreateUser from "../modals/CreateUser";
import Footer from "../common/Footer";
import SkeletonLoader, { DashboardCardsSkeleton, TableDataSkeleton } from "../../components/SkeletonLoader";
import { FaUsers, FaTicketAlt, FaHandHoldingUsd, FaUserPlus } from "react-icons/fa";
import DataGrid from "../../components/common/DataGrid";

const AdminHome = () => {
  const userToken = localStorage.getItem("userToken");
  const [ngos, setNgos] = useState([]);
  const [filter, setfilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [transType, setTransType] = useState("");
  const [userData, setUserData] = useState({});
  const [sDate, setSDate] = useState("");
  const [eDate, setEDate] = useState("");
  const [expiry, setExpiry] = useState(0);
  const [currencyVal, setCurrencyVal] = useState(0);
  const [exChangeRate, setExChangeRate] = useState(0);
  const [startDateVal, setStartDateVal] = useState({
    value: moment().format("YYYY-MM-DD"),
  });
  const [isDashboardLoading, setIsDashboardLoading] = useState(true);
  const [isTableDataLoading, setIsTableDataLoading] = useState(true);
  const [profile, setProfile] = useState(localStorage.getItem("userRole") || "");

  const isInitialLoad = useRef(true);

  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;
  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };
  const indexOfLastItem = (currentPage + 1) * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = ngos?.slice(indexOfFirstItem, indexOfLastItem);

  const loadExpire = () => {
    let config = {
      method: "get",
      maxBodyLength: Infinity,
      url: `${process.env.REACT_APP_LOCAL_API}/expiry`,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };

    axios
      .request(config)
      .then((response) => {
        let totalAmount = 0;
        response?.data?.upcomingExpiries?.forEach((transaction) => {
          totalAmount += transaction.amount || 0;
        });
        setExpiry(totalAmount);
      })
      .catch(() => {});
  };

  const loadData = (searchValue = searchTerm) => {
    let config = {
      method: "get",
      maxBodyLength: Infinity,
      url: `${process.env.REACT_APP_LOCAL_API}/admindashboard?profileType=${filter}&searchTerm=${searchValue}`,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };

    axios
      .request(config)
      .then((response) => {
        setNgos(response.data.data);
      })
      .catch(() => {})
      .finally(() => {
        setIsTableDataLoading(false);
      });
  };

  const handleSearch = () => {
    setIsTableDataLoading(true);
    setCurrentPage(0); // Reset to first page when searching
    loadData(searchTerm);
  };

  const loadUserData = () => {
    let config = {
      method: "get",
      maxBodyLength: Infinity,
      url: `${process.env.REACT_APP_LOCAL_API}/dashboard`,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };

    axios
      .request(config)
      .then((response) => {
        setUserData(response.data);
      })
      .catch((error) => { })
      .finally(() => {
        setIsDashboardLoading(false);
      });
  };

  const loadCoin = () => {
    let config = {
      method: "get",
      maxBodyLength: Infinity,
      url: `${process.env.REACT_APP_LOCAL_API}/currency`,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };

    axios
      .request(config)
      .then((response) => {
        setExChangeRate(response.data[0]?.value?.toFixed(2));
        setCurrencyVal(response.data[0]?.value);
      })
      .catch(() => {});
  };

  // Fetch user profile
  useEffect(() => {
    let config = {
      method: "get",
      maxBodyLength: Infinity,
      url: `${process.env.REACT_APP_LOCAL_API}/user`,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };

    axios
      .request(config)
      .then((response) => {
        const profileType = response?.data?.user?.profileType;
        setProfile(profileType);
        // Update localStorage with the fetched role
        if (profileType) {
          localStorage.setItem("userRole", profileType);
        }
      })
      .catch(() => {});
  }, [userToken]);

  // Effect for data loading - handles both initial load and filter changes
  useEffect(() => {
    if (isInitialLoad.current) {
      // Initial load - load all data
      setIsTableDataLoading(true);
      loadCoin();
      loadData();
      loadUserData();
      loadExpire();
      isInitialLoad.current = false;
    } else {
      // Subsequent loads - only reload table data for filter changes
      setIsTableDataLoading(true);
      loadData();
    }
  }, [filter]);

  const handleCoin = () => {
    let data = JSON.stringify({
      currency: "usd",
      value: exChangeRate,
      startDate: startDateVal?.value,
    });

    let config = {
      method: "post",
      maxBodyLength: Infinity,
      url: `${process.env.REACT_APP_LOCAL_API}/currency`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
      data: data,
    };

    axios
      .request(config)
      .then((response) => {
        window.location.reload();
        Swal.fire({
          title: "Success!",
          confirmButtonColor: "#3ca151",
          text: "Coin Value Changed Successfully!",
          icon: "success",
        });
      })
      .catch((error) => {
        Swal.fire({
          title: "Error",
          confirmButtonColor: "#3ca151",
          text: "Something Went Wrong!",
          icon: "error",
        });
      });
  };

  const downloadData = async (endpoint, name) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_LOCAL_API}${endpoint}`,
        {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${userToken}`,
          },
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", name);
      document.body.appendChild(link);

      link.click();
    } catch (error) {
      console.error("Error downloading data:", error);
    }
  };

  const handleDownload = async (e) => {
    e.preventDefault();
    downloadData(
      `/report?startDate=${sDate}&endDate=${eDate}&transactionType=${transType}`,
      "Transaction_Report.csv"
    );
    downloadData(`/userreport`, "User_List.csv");
  };

  const getCurrentDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const validateOrg = (user) => {
    let data = JSON.stringify({ isVerified: !user.isVerified });
    let config = {
      method: "patch",
      maxBodyLength: Infinity,
      url: `${process.env.REACT_APP_LOCAL_API}/user/${user._id}`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
      data: data,
    };

    axios
      .request(config)
      .then(async (response) => {
        await Swal.fire({
          title: "Success!",
          text: "User Updated Successfully!",
          icon: "success",
          confirmButtonColor: "#3ca151",
        });
        loadData();
      })
      .catch((error) => {
        Swal.fire({
          title: "Error!",
          confirmButtonColor: "#3ca151",
          text: error.response?.data?.message || "Something Went Wrong!",
          icon: "error",
        });
      });
  };

  const updateUserProfileType = (userId, newProfileType) => {
    let data = JSON.stringify({ profileType: newProfileType });
    let config = {
      method: "patch",
      maxBodyLength: Infinity,
      url: `${process.env.REACT_APP_LOCAL_API}/user/${userId}`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
      data: data,
    };

    axios
      .request(config)
      .then(async (response) => {
        await Swal.fire({
          title: "Success!",
          text: "User role updated successfully!",
          icon: "success",
          confirmButtonColor: "#3ca151",
        });
        loadData();
      })
      .catch((error) => {
        Swal.fire({
          title: "Error!",
          confirmButtonColor: "#3ca151",
          text: error.response?.data?.message || "Something Went Wrong!",
          icon: "error",
        });
      });
  };

  return (
    <>
      <div style={{ margin: "100px auto", maxWidth: "1600px", padding: "0 20px", boxSizing: "border-box" }}>
        <h1 id="head_dash" style={{ margin: "30px 0", textAlign: "left" }}>
          Admin Dashboard
        </h1>

        {/* Key Metrics Grid - At-a-Glance Widgets */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: "24px",
          marginBottom: "32px"
        }}>
          {/* Total Active Users */}
          <div className="subscription-card" style={{ background: "rgba(255, 255, 255, 0.05)", backdropFilter: "blur(10px)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
              <div>
                <p style={{ margin: 0, fontSize: "14px", color: "#a0a0a0", fontWeight: "500" }}>Total Active Users</p>
                <h3 style={{ margin: "8px 0 0", fontSize: "28px", color: "#fff", fontWeight: "700" }}>{ngos?.length || 0}</h3>
              </div>
              <div style={{ padding: "12px", borderRadius: "12px", background: "rgba(60, 161, 81, 0.1)", color: "#3ca151" }}>
                <FaUsers size={20} />
              </div>
            </div>
            <div style={{ fontSize: "12px", color: "#a0a0a0" }}>
              <span style={{ color: "#3ca151", fontWeight: "600" }}>+2.5%</span> from last month
            </div>
          </div>

          {/* Pending Support Tickets */}
          <div className="subscription-card" style={{ background: "rgba(255, 255, 255, 0.05)", backdropFilter: "blur(10px)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
              <div>
                <p style={{ margin: 0, fontSize: "14px", color: "#a0a0a0", fontWeight: "500" }}>Pending Tickets</p>
                <h3 style={{ margin: "8px 0 0", fontSize: "28px", color: "#fff", fontWeight: "700" }}>5</h3>
              </div>
              <div style={{ padding: "12px", borderRadius: "12px", background: "rgba(234, 179, 8, 0.1)", color: "#eab308" }}>
                <FaTicketAlt size={20} />
              </div>
            </div>
            <div style={{ fontSize: "12px", color: "#a0a0a0" }}>
              Requires attention
            </div>
          </div>

          {/* Monthly Revenue/Donations */}
          <div className="subscription-card" style={{ background: "rgba(255, 255, 255, 0.05)", backdropFilter: "blur(10px)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
              <div>
                <p style={{ margin: 0, fontSize: "14px", color: "#a0a0a0", fontWeight: "500" }}>Monthly Volume</p>
                <h3 style={{ margin: "8px 0 0", fontSize: "28px", color: "#fff", fontWeight: "700" }}>
                  ${((ngos?.reduce((acc, curr) => acc + (curr.totalSent || 0) + (curr.totalReceived || 0), 0) / 2) || 0).toLocaleString(undefined, { maximumFractionDigits: 0, notation: "compact" })}
                </h3>
              </div>
              <div style={{ padding: "12px", borderRadius: "12px", background: "rgba(59, 130, 246, 0.1)", color: "#3b82f6" }}>
                <FaHandHoldingUsd size={20} />
              </div>
            </div>
            <div style={{ fontSize: "12px", color: "#a0a0a0" }}>
              Total transaction volume
            </div>
          </div>

          {/* New Registrations */}
          <div className="subscription-card" style={{ background: "rgba(255, 255, 255, 0.05)", backdropFilter: "blur(10px)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
              <div>
                <p style={{ margin: 0, fontSize: "14px", color: "#a0a0a0", fontWeight: "500" }}>New Registrations</p>
                <h3 style={{ margin: "8px 0 0", fontSize: "28px", color: "#fff", fontWeight: "700" }}>
                  {ngos?.filter(u => u.user?.createdAt && moment(u.user.createdAt).isSame(moment(), 'week'))?.length || 0}
                </h3>
              </div>
              <div style={{ padding: "12px", borderRadius: "12px", background: "rgba(168, 85, 247, 0.1)", color: "#a855f7" }}>
                <FaUserPlus size={20} />
              </div>
            </div>
            <div style={{ fontSize: "12px", color: "#a0a0a0" }}>
              <span style={{ color: "#3ca151", fontWeight: "600" }}>+12%</span> this week
            </div>
          </div>
        </div>

        {/* Dashboard Cards Section */}
        {isDashboardLoading ? (
          <DashboardCardsSkeleton />
        ) : (
          <div
            id="adminboxwrapper"
            style={{
              display: "flex",
              flexDirection: "row",
              alignContent: "center",
              justifyContent: "space-between",
              gap: "20px",
              marginBottom: "20px",
            }}
          >
            <div className="shadow-box box-width-correct">
              <h3>
                {userData?.user?.firstName} {userData?.user?.lastName}
                <br />
                <span id="today">
                  ({userData?.user?.profileType.toUpperCase()})
                </span>
                <br />
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                  Referral Code: {userData?.user?.referCode}
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(userData?.user?.referCode || "");
                      Swal.fire({
                        icon: 'success',
                        title: 'Copied!',
                        text: 'Referral code copied to clipboard',
                        timer: 1500,
                        showConfirmButton: false
                      });
                    }}
                    style={{
                      marginLeft: "10px",
                      background: "transparent",
                      border: "1px solid #3ca151",
                      color: "#3ca151",
                      borderRadius: "4px",
                      padding: "2px 8px",
                      cursor: "pointer",
                      fontSize: "12px",
                      display: "flex",
                      alignItems: "center"
                    }}
                  >
                    Copy
                  </button>
                </span>
              </h3>
              <h3>
                Balance: ${" "}
                {(userData?.wallet?.walletPoint * currencyVal).toLocaleString(
                  undefined,
                  {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }
                )}{" "}
                <span id="today">
                  (1.000 DM = ${currencyVal?.toFixed(2)}){" "}
                  {
                    //<a id="today" href='/currency'>Price History</a>
                  }
                </span>
              </h3>
              <h3>
                No. of DM:{" "}
                {userData?.wallet?.walletPoint?.toLocaleString(undefined, {
                  minimumFractionDigits: 3,
                  maximumFractionDigits: 3,
                })}
              </h3>
            </div>

            {profile !== "view_access" && (
              <div className="shadow-box box-width-correct">
                <h2>Reports</h2>
                <form onSubmit={handleDownload}>
                  <label htmlFor="sDate">Start Date:</label>
                  <input
                    type="date"
                    id="sDate"
                    name="sDate"
                    value={sDate}
                    onChange={(e) => setSDate(e.target.value)}
                  />
                  <label htmlFor="eDate">End Date:</label>
                  <input
                    type="date"
                    id="eDate"
                    name="eDate"
                    value={eDate}
                    onChange={(e) => setEDate(e.target.value)}
                  />
                  <select onChange={(e) => setTransType(e.target.value)}>
                    <option value="">Type</option>
                    <option value="Donation">Donation</option>
                    <option value="Grant">Grant</option>
                    <option value="Sale">Sale</option>
                    <option value="Purchase">Purchase</option>
                    <option value="Barter">Barter</option>
                    <option value="Gift">Gift</option>

                    {/* Add more options as needed */}
                  </select>

                  <button type="submit">Download Report</button>
                </form>
              </div>
            )}

            <div className="shadow-box box-width-correct">
              <h3>Exchange Rate</h3>

              <input
                name="currencyValue"
                value={exChangeRate}
                type="number"
                disabled={profile === "view_access"}
                onChange={(e) => {
                  setExChangeRate(e.target.value);
                }}
              />
              <input
                name="currencyDate"
                value={startDateVal?.value}
                type="date"
                min={getCurrentDate()}
                disabled={profile === "view_access"}
                onChange={(e) => setStartDateVal({ value: e.target.value })}
              />
              {profile !== "view_access" && (
                <button type="button" onClick={handleCoin}>
                  Change
                </button>
              )}
            </div>
          </div>
        )}

        {profile !== "view_access" && (
          <div className="shadow-box admin-box" style={{ width: "100%", margin: "0 0 20px 0", boxSizing: "border-box" }}>
            <h3>Create New User!</h3>
            <CreateUser userId={userData?.user?._id} ngo={false} />
          </div>
        )}

        {/* Users Table Section */}
        {isTableDataLoading ? (
          <TableDataSkeleton />
        ) : (
          <div
            className="shadow-box admin-box"
            style={{ width: "100%", marginTop: "0", boxSizing: "border-box" }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              <h3>All Users</h3>
              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <div style={{ display: "flex", gap: "5px" }}>
                  <input
                    placeholder="Search user by name"
                    style={{
                      padding: "12px 15px",
                      fontSize: "14px",
                      width: "250px",
                      height: "42px",
                      boxSizing: "border-box"
                    }}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch();
                      }
                    }}
                  />
                  <button
                    onClick={handleSearch}
                    style={{
                      padding: "0 20px",
                      backgroundColor: "#3ca151",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontWeight: "bold",
                      height: "42px",
                      fontSize: "14px",
                      boxSizing: "border-box"
                    }}
                  >
                    Search
                  </button>
                </div>
                <select onChange={(e) => setfilter(e.target.value)}>
                  <option value="">All</option>
                  <option value="admin">Admin</option>
                  <option value="ngo">NGO</option>
                  <option value="impactOrg">Impact Organisation</option>
                  <option value="individual">Individual</option>
                  <option value="business">Business</option>
                  <option value="view_access">View Access</option>
                </select>
              </div>
            </div>
            <hr style={{ marginBottom: "20px" }} />
            {isTableDataLoading ? (
              <TableDataSkeleton />
            ) : (
              <div className="table" style={{ overflow: 'visible' }}>
                <DataGrid
                  loading={false}
                  data={currentItems}
                  pagination={{
                    pageCount: Math.ceil(ngos?.length / itemsPerPage),
                    currentPage: currentPage,
                  }}
                  onPageChange={handlePageChange}
                  columns={[
                    {
                      header: 'Name',
                      accessor: 'user.businessName', // Sort key
                      sortable: true,
                      noWrap: true,
                      render: (ngo) => (
                        <a href={`/admin-user/${ngo.user?._id}`} style={{ color: '#4caf50', textDecoration: 'none', fontWeight: '500' }}>
                          {ngo.user.profileType === "ngo" ||
                            ngo.user.profileType === "impactOrg" ||
                            ngo.user.profileType === "business"
                            ? (ngo.user.businessName || ngo.user?.firstName + " " + ngo.user?.lastName)
                            : (ngo.user?.firstName + " " + ngo.user?.lastName).trim() || ngo.user.businessName || "N/A"}
                        </a>
                      )
                    },
                    {
                      header: 'Email',
                      accessor: 'user.email',
                      sortable: true,
                      noWrap: true,
                      render: (ngo) => ngo.user?.email || "N/A"
                    },
                    {
                      header: 'EIN',
                      accessor: 'user.ein',
                      sortable: false,
                      noWrap: true,
                      render: (ngo) => ngo.user?.ein || "-"
                    },
                    {
                      header: 'Available DM',
                      accessor: 'wallet.walletPoint',
                      sortable: true,
                      noWrap: true,
                      render: (ngo) => ngo.wallet?.walletPoint?.toLocaleString(undefined, {
                        minimumFractionDigits: 3,
                        maximumFractionDigits: 3,
                      }) || "0"
                    },
                    {
                      header: 'DM Sent',
                      accessor: 'totalSent',
                      sortable: true,
                      noWrap: true,
                      render: (ngo) => ngo?.totalSent?.toLocaleString(undefined, {
                        minimumFractionDigits: 3,
                        maximumFractionDigits: 3,
                      }) || "0"
                    },
                    {
                      header: 'DM Received',
                      accessor: 'totalReceived',
                      sortable: true,
                      noWrap: true,
                      render: (ngo) => ngo?.totalReceived?.toLocaleString(undefined, {
                        minimumFractionDigits: 3,
                        maximumFractionDigits: 3,
                      }) || "0"
                    },
                    {
                      header: 'Status',
                      accessor: 'status', // Virtual accessor
                      sortable: false,
                      noWrap: true,
                      render: (ngo) => {
                        const profileType = ngo?.user?.profileType;
                        let isVerified = false;
                        if (profileType === "individual" || profileType === "view_access") {
                          isVerified = ngo?.user?.isEmailVerified;
                        } else if (profileType === "ngo" || profileType === "business" || profileType === "impactOrg") {
                          isVerified = ngo?.user?.isVerified;
                        }

                        // Semantic Colors: Green for Verified, Orange for Not Verified (Pending)
                        const statusColor = isVerified ? '#16a34a' : '#ea580c'; // green-600 : orange-600
                        const statusBg = isVerified ? 'rgba(22, 163, 74, 0.1)' : 'rgba(234, 88, 12, 0.1)';
                        const statusBorder = isVerified ? 'rgba(22, 163, 74, 0.2)' : 'rgba(234, 88, 12, 0.2)';

                        return (
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '9999px', // Fully rounded pill
                            background: statusBg,
                            color: statusColor,
                            fontSize: '12px',
                            fontWeight: '600',
                            border: `1px solid ${statusBorder}`,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '90px'
                          }}>
                            {isVerified ? "Verified" : "Not Verified"}
                          </span>
                        );
                      }
                    },
                    (profile === "admin" || profile === "view_access") && {
                      header: 'Role',
                      accessor: 'user.profileType',
                      sortable: true,
                      noWrap: true,
                      render: (ngo) => {
                        const currentRole = ngo?.user?.profileType || "individual";
                        return (
                          <span style={{ textTransform: 'capitalize', color: '#cbd5f5' }}>
                            {currentRole === "ngo" ? "NGO" :
                              currentRole === "business" ? "Business" :
                                currentRole === "impactOrg" ? "Impact Org" :
                                  currentRole === "admin" ? "Admin" :
                                    currentRole === "view_access" ? "View Access" :
                                      "Individual"}
                          </span>
                        );
                      }
                    },
                    profile === "admin" && {
                      header: 'Actions',
                      accessor: 'actions',
                      sortable: false,
                      noWrap: true,
                      render: (ngo, ActionMenu) => {
                        const currentRole = ngo?.user?.profileType || "individual";
                        const restrictedRoles = ["ngo", "business", "impactOrg", "admin"];
                        const isRestricted = restrictedRoles.includes(currentRole);

                        // Check if ANY action is available to decide if we renders the menu
                        const canValidate = isRestricted && currentRole !== "admin" && profile !== "view_access";
                        const canRevoke = currentRole === "view_access";
                        const canPromote = currentRole === "individual";

                        if (!canValidate && !canRevoke && !canPromote) return "-";

                        return (
                          <ActionMenu>
                            {canValidate && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevent row click
                                  validateOrg(ngo?.user);
                                }}
                                style={{
                                  display: 'block',
                                  width: '100%',
                                  padding: '10px 16px',
                                  textAlign: 'left',
                                  background: 'transparent',
                                  border: 'none',
                                  color: '#fff',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  transition: 'background 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.05)'}
                                onMouseLeave={(e) => e.target.style.background = 'transparent'}
                              >
                                {ngo?.user?.isVerified ? "Hide Profile" : "Validate Profile"}
                              </button>
                            )}

                            {canRevoke && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  Swal.fire({
                                    title: "Remove View Access Role",
                                    html: `<p>Are you sure?</p>`,
                                    icon: "warning",
                                    showCancelButton: true,
                                    confirmButtonText: "Yes",
                                    confirmButtonColor: "#d33",
                                  }).then((result) => {
                                    if (result.isConfirmed) {
                                      updateUserProfileType(ngo?.user?._id, "individual");
                                    }
                                  });
                                }}
                                style={{
                                  display: 'block',
                                  width: '100%',
                                  padding: '10px 16px',
                                  textAlign: 'left',
                                  background: 'transparent',
                                  border: 'none',
                                  color: '#f87171',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  transition: 'background 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.background = 'rgba(248, 113, 113, 0.1)'}
                                onMouseLeave={(e) => e.target.style.background = 'transparent'}
                              >
                                Revoke Access
                              </button>
                            )}

                            {canPromote && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  Swal.fire({
                                    title: "Assign View Access Role",
                                    html: `<p>Are you sure?</p>`,
                                    icon: "info",
                                    showCancelButton: true,
                                    confirmButtonText: "Yes",
                                    confirmButtonColor: "#3ca151",
                                  }).then((result) => {
                                    if (result.isConfirmed) {
                                      updateUserProfileType(ngo?.user?._id, "view_access");
                                    }
                                  });
                                }}
                                style={{
                                  display: 'block',
                                  width: '100%',
                                  padding: '10px 16px',
                                  textAlign: 'left',
                                  background: 'transparent',
                                  border: 'none',
                                  color: '#4caf50',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  transition: 'background 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.background = 'rgba(76, 175, 80, 0.1)'}
                                onMouseLeave={(e) => e.target.style.background = 'transparent'}
                              >
                                Promote to Viewer
                              </button>
                            )}
                          </ActionMenu>
                        )
                      }
                    }
                  ].filter(Boolean)}
                />
              </div>
            )}
          </div>
        )}
      </div>
      <Footer />
    </>
  );
};

export default AdminHome;
